import { useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ModalsProvider, SelectField } from '@speeki/global-ui-components';
import { IconManager } from '@speeki/icons';

import { FieldWrapper } from '@components/atoms/FieldWrapper/FieldWrapper';

import {
    Country_Code,
    CountryAnonymityValidateDocument,
} from '@graphql/generated/graphql';

import { countriesOptions } from '@utils/dictionary';
import { useCaseOwnerExclusion } from '@utils/hooks/useCaseOwnerExclusion';

import { useLazyQuery } from '@apollo/client';
import {
    FormWrapper,
    InfoText,
    InputsWrapper,
    ReviewerInfoText,
    ReviewerInfoWrapper,
    ReviewerNameText,
} from '@domains/new-conversation/organisms/ConversationDetailsForm/ConversationDetailsForm.styled';
import { PersonalInformationForm } from '@domains/new-conversation/organisms/ConversationDetailsForm/PersonalInformationForm';
import {
    DetailsSchemaForm,
    useConversationForm,
} from '@domains/new-conversation/useConversationForm';

export const ConversationDetailsForm = () => {
    const { t } = useTranslation();
    const {
        exclusionOwnerList,
        incidentTypesList,
        isExclusionOwnerEnabled,
        isPersonalInfoMandatory,
        setIsPersonalInfoMandatory,
        setShowPersonalInformation,
        showPersonalInformation,
    } = useConversationForm();

    const {
        control,
        formState: { errors, isSubmitted },
        setValue,
        trigger,
        watch,
    } = useFormContext<DetailsSchemaForm>();

    const { data: ownerExclusionData } = useCaseOwnerExclusion();
    const fallbackUserFullName =
        ownerExclusionData?.tenant.caseOwnerExclusion?.fallbackUser.fullName;

    const [countryAnonymityValidate, { data }] = useLazyQuery(
        CountryAnonymityValidateDocument,
        {
            context: {
                notifyOnError: true,
            },
        },
    );

    const isRestricted = data?.isRestricted;
    const country = watch('country');
    const incidentType = watch('incidentTypeUuid');

    const renderPersonalInformation =
        !!country?.label &&
        !!incidentType?.label &&
        isPersonalInfoMandatory !== undefined;

    useEffect(() => {
        if (showPersonalInformation && !isPersonalInfoMandatory) {
            setValue('email', undefined);
            setValue('firstName', undefined);
            setValue('lastName', undefined);
        }
        if (showPersonalInformation && isSubmitted) {
            void trigger?.();
        }
    }, [showPersonalInformation]);

    useEffect(() => {
        if (!!country?.label && !!incidentType?.label) {
            void countryAnonymityValidate({
                variables: {
                    input: {
                        country: country.value as Country_Code,
                        incidentTypeUuid: incidentType.value,
                    },
                },
            });

            if (isRestricted) {
                setShowPersonalInformation(true);
                setValue('personalInfoMandatory', true);
            } else setValue('personalInfoMandatory', false);
        }

        setIsPersonalInfoMandatory(isRestricted);
    }, [country, incidentType, isRestricted]);

    const { field: countrySelect } = useController({
        control,
        name: 'country',
    });

    const { field: incidentSelect } = useController({
        control,
        name: 'incidentTypeUuid',
    });

    const { field: caseExclusionOwnerListSelect } = useController({
        control,
        name: 'caseExclusionOwnerList',
    });

    return (
        <FormWrapper>
            <InputsWrapper>
                <FieldWrapper>
                    <SelectField
                        {...countrySelect}
                        error={!!errors.country}
                        label={`${t('COUNTRY_INCIDENT')} *`}
                        message={
                            errors.country?.message && t(errors.country.message)
                        }
                        options={countriesOptions()}
                        placeholder={t('SELECT_COUNTRY')}
                        testId="select_conversation-details-country"
                    />
                </FieldWrapper>
                <FieldWrapper>
                    <SelectField
                        {...incidentSelect}
                        clickableLabel={{
                            onClick: () =>
                                ModalsProvider.open('IncidentTypesModal'),
                            text: t('INCIDENT_TYPES_LABEL'),
                        }}
                        error={!!errors.incidentTypeUuid}
                        isDisabled={!country?.label}
                        label={`${t('INCIDENT_TYPE')} *`}
                        message={
                            errors.incidentTypeUuid?.message &&
                            t(errors.incidentTypeUuid.message)
                        }
                        options={incidentTypesList}
                        placeholder={t('SELECT_INCIDENT_TYPE')}
                        testId="select_conversation-details-incident"
                    />
                </FieldWrapper>
            </InputsWrapper>
            {renderPersonalInformation && (
                <PersonalInformationForm
                    isShown={showPersonalInformation}
                    key={JSON.stringify(incidentType)}
                    mandatory={!!isPersonalInfoMandatory}
                    showForm={setShowPersonalInformation}
                />
            )}
            {isExclusionOwnerEnabled && (
                <InputsWrapper>
                    <InfoText>{t('EXCLUDE_INFO')}</InfoText>
                    <FieldWrapper>
                        <SelectField
                            {...caseExclusionOwnerListSelect}
                            closeMenuOnSelect={false}
                            error={!!errors.caseExclusionOwnerList}
                            errorMessage={t(
                                // @ts-expect-error TODO check what is wrong with error select type
                                errors.caseExclusionOwnerList?.value?.message,
                            )}
                            isMulti
                            label={t('EXCLUDE_CASE_OWNER')}
                            /*@ts-expect-error FIXME: Select value can only be string, add more option in package*/
                            options={exclusionOwnerList}
                            placeholder={t('SELECT_CASE_OWNER')}
                            testId="select_conversation-details-case-exclusion"
                        />
                    </FieldWrapper>
                    <ReviewerInfoWrapper>
                        <IconManager
                            icon="SvgInfo"
                            size={16}
                            themeColors={{
                                default: {
                                    'color-1': 'text.primary',
                                },
                            }}
                        />
                        <ReviewerInfoText>
                            {t('FALLBACK_USER_INFO_PART_1')}{' '}
                            <ReviewerNameText>
                                {fallbackUserFullName}
                            </ReviewerNameText>{' '}
                            {t('FALLBACK_USER_INFO_PART_2')}
                        </ReviewerInfoText>
                    </ReviewerInfoWrapper>
                </InputsWrapper>
            )}
        </FormWrapper>
    );
};
